import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { StatusIndicator, PriorityBadge } from '@/modules/wms/components/common/StatusIndicator';
import { ScanInput } from '@/modules/wms/components/common/ScanInput';
import { receivingAPI } from '@/shared/services/api';
import {
  Search,
  Package,
  MapPin,
  AlertTriangle,
  FileText,
  ArrowRight,
  Scan,
  ClipboardList,
  CheckCircle2,
  Loader2 } from
'lucide-react';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger } from
'@/shared/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter } from
'@/shared/components/ui/dialog';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { toast } from 'sonner';

export const Putaway = () => {const { t } = useTranslation("wms");
  const [selectedItem, setSelectedItem] = useState(null);
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ pending: 0, quarantine: 0, stored: 0 });

  // Fetch putaway tasks from backend
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const tasksData = await receivingAPI.getPutawayTasks();
        setTasks(tasksData || []);
        setStats({
          pending: (tasksData || []).filter((t) => t.status === 'pending' || t.status === 'assigned').length,
          quarantine: (tasksData || []).filter((t) => t.status === 'quarantine').length,
          stored: (tasksData || []).filter((t) => t.status === 'completed').length
        });
      } catch (error) {
        console.error('Failed to fetch putaway tasks:', error);
        toast.error('Failed to load putaway tasks');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleScan = (barcode) => {
    console.log('Scanned item:', barcode);
    // Would lookup item and open assignment dialog
  };

  const handleAssignLocation = (task) => {
    setSelectedItem(task);
    setShowLocationDialog(true);
  };

  return (
    <div className="space-y-6" data-testid="wms-putaway">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("putaway", "Put-away")}</h1>
          <p className="text-muted-foreground mt-1">{t("assignStorageLocations", "Assign storage locations for received items")}

          </p>
        </div>
        <Button variant="outline" className="gap-2">
          <FileText className="h-4 w-4" />{t("generatePutawayReport", "Generate Put-away Report")}

        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card className="card-elevated border-l-4 border-l-amber-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t("pendingItems", "Pending Items")}</p>
              <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
            </div>
            <Package className="h-8 w-8 text-amber-500/30" />
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-red-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t("quarantinedItems", "Quarantined Items")}</p>
              <p className="text-2xl font-bold text-red-600">{stats.quarantine}</p>
            </div>
            <AlertTriangle className="h-8 w-8 text-red-500/30" />
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-green-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t("storedItems", "Stored Items")}</p>
              <p className="text-2xl font-bold text-green-600">{stats.stored || 1}</p>
            </div>
            <CheckCircle2 className="h-8 w-8 text-green-500/30" />
          </CardContent>
        </Card>
      </div>

      {/* Scan Input */}
      <Card className="card-elevated bg-gradient-to-r from-purple-50/50 to-transparent dark:from-purple-950/20">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/50">
              <Scan className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold">{t("scanItemFor", "Scan Item for Put-away")}</h3>
              <p className="text-sm text-muted-foreground">{t("scanItemBarcode", "Scan item barcode to begin put-away process")}</p>
            </div>
          </div>
          <ScanInput
            placeholder="Scan item barcode or enter SKU..."
            onScan={handleScan} />
          
        </CardContent>
      </Card>

      {/* Alert */}
      {stats.pending > 0 &&
      <Card className="card-elevated border-l-4 border-l-blue-500 bg-blue-50/50 dark:bg-blue-950/20">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/50">
              <ClipboardList className="h-5 w-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm">{t("thereAre", "There are")}
              <span className="font-semibold">{stats.pending}{t("items", "items")}</span>{t("pendingPutawayScan", "pending put-away. \n                Scan items or select from the list above to assign storage locations.")}

            </p>
            </div>
          </CardContent>
        </Card>
      }

      {/* Put-away Tasks */}
      <Tabs defaultValue="pending" className="space-y-4">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="pending" className="gap-2">{t("pending", "Pending")}

            <Badge variant="secondary" className="ml-1">{stats.pending}</Badge>
          </TabsTrigger>
          <TabsTrigger value="quarantine" className="gap-2">{t("quarantine", "Quarantine")}

            <Badge variant="destructive" className="ml-1">{stats.quarantine}</Badge>
          </TabsTrigger>
          <TabsTrigger value="all">{t("allItems", "All Items")}</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="space-y-4">
          <Card className="card-elevated">
            <CardHeader className="pb-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <CardTitle className="text-lg">{t("pendingPutaway", "Pending Put-away")}</CardTitle>
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by SKU or product name"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10" />
                  
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("sku", "SKU")}</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("productName", "Product Name")}</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("quantity", "Quantity")}</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("batchNumber", "Batch Number")}</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("priority", "Priority")}</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("status", "Status")}</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("suggestedLocation", "Suggested Location")}</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("actions", "Actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ?
                    <tr>
                        <td colSpan={8} className="py-8 text-center">
                          <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                          <p className="text-sm text-muted-foreground mt-2">{t("loadingTasks", "Loading tasks...")}</p>
                        </td>
                      </tr> :
                    tasks.filter((t) => t.status === 'pending' || t.status === 'in_progress' || t.status === 'assigned').length === 0 ?
                    <tr>
                        <td colSpan={8} className="py-8 text-center">
                          <Package className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
                          <p className="text-sm text-muted-foreground">{t("noPendingPutaway", "No pending putaway tasks")}</p>
                        </td>
                      </tr> :
                    tasks.filter((t) => t.status === 'pending' || t.status === 'in_progress' || t.status === 'assigned').map((task) => {
                      const taskId = task.id || task.task_id;
                      return (
                        <tr key={taskId} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                          <td className="py-4 px-4">
                            <span className="font-mono text-sm font-medium">{task.sku || task.product_sku}</span>
                          </td>
                          <td className="py-4 px-4">
                            <p className="text-sm font-medium">{task.productName || task.product_name || task.sku}</p>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <span className="font-medium">{task.quantity || task.qty || 0}</span>
                          </td>
                          <td className="py-4 px-4">
                            <span className="font-mono text-sm text-muted-foreground">{task.batchNumber || task.lot_number || '-'}</span>
                          </td>
                          <td className="py-4 px-4">
                            <PriorityBadge priority={task.priority || 'medium'} />
                          </td>
                          <td className="py-4 px-4">
                            <StatusIndicator status={task.status} />
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-1.5">
                              <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                              <span className="font-mono text-sm">{task.suggestedLocation || task.suggested_location || task.target_location || '-'}</span>
                            </div>
                          </td>
                          <td className="py-4 px-4 text-right">
                            <Button
                              size="sm"
                              onClick={() => handleAssignLocation(task)}
                              data-testid={`assign-location-${taskId}`}>{t("assignLocation", "Assign Location")}


                              <ArrowRight className="h-4 w-4 ml-1" />
                            </Button>
                          </td>
                        </tr>);

                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="quarantine">
          <Card className="card-elevated">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("sku", "SKU")}</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("productName", "Product Name")}</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("quantity", "Quantity")}</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("reason", "Reason")}</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("status", "Status")}</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("actions", "Actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tasks.filter((t) => t.status === 'quarantine').length === 0 ?
                    <tr>
                        <td colSpan={6} className="py-8 text-center">
                          <AlertTriangle className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
                          <p className="text-sm text-muted-foreground">{t("noQuarantinedItems", "No quarantined items")}</p>
                        </td>
                      </tr> :
                    tasks.filter((t) => t.status === 'quarantine').map((task) => {
                      const taskId = task.id || task.task_id;
                      return (
                        <tr key={taskId} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                          <td className="py-4 px-4">
                            <span className="font-mono text-sm font-medium">{task.sku || task.product_sku}</span>
                          </td>
                          <td className="py-4 px-4">
                            <p className="text-sm font-medium">{task.productName || task.product_name || task.sku}</p>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <span className="font-medium">{task.quantity || task.qty || 0}</span>
                          </td>
                          <td className="py-4 px-4">
                            <span className="text-sm text-muted-foreground">{task.notes || task.reason || '-'}</span>
                          </td>
                          <td className="py-4 px-4">
                            <StatusIndicator status={task.status} />
                          </td>
                          <td className="py-4 px-4 text-right">
                            <Button size="sm" variant="outline">{t("review", "Review")}</Button>
                          </td>
                        </tr>);

                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="all">
          <Card className="card-elevated">
            <CardContent className="p-8 text-center">
              <ClipboardList className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
              <h3 className="font-semibold mb-2">{t("allItemsView", "All Items View")}</h3>
              <p className="text-sm text-muted-foreground">{t("viewAllPutaway", "View all put-away history and current tasks")}

              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Location Assignment Dialog */}
      <Dialog open={showLocationDialog} onOpenChange={setShowLocationDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("assignStorageLocation", "Assign Storage Location")}</DialogTitle>
          </DialogHeader>
          {selectedItem &&
          <div className="space-y-4">
              <div className="p-4 rounded-lg bg-muted/50">
                <p className="text-sm text-muted-foreground">{t("item", "Item")}</p>
                <p className="font-medium">{selectedItem.productName}</p>
                <div className="flex gap-4 mt-2 text-sm">
                  <span><strong>{t("qty", "Qty:")}</strong> {selectedItem.quantity}</span>
                  <span><strong>{t("sku", "SKU:")}</strong> {selectedItem.sku}</span>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium mb-2 block">{t("scanStorageLocation", "Scan Storage Location")}</label>
                  <div className="flex gap-2">
                    <Input placeholder="Scan or enter location code" className="flex-1" />
                    <Button variant="outline">
                      <Scan className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">{t("orSelectLocation", "Or Select Location")}</label>
                  <select className="w-full h-10 px-3 border border-input rounded-md bg-background">
                    <option value="">{t("selectAWarehouse", "Select a warehouse location")}</option>
                    <option value={selectedItem.suggestedLocation}>{selectedItem.suggestedLocation}{t("suggested", "(Suggested)")}</option>
                    <option value="A-01-002">{t("a01002", "A-01-002")}</option>
                    <option value="B-02-001">{t("b02001", "B-02-001")}</option>
                    <option value="C-01-003">{t("c01003", "C-01-003")}</option>
                  </select>
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox id="quarantine" />
                  <label htmlFor="quarantine" className="text-sm">{t("quarantineRequired", "Quarantine Required?")}</label>
                </div>
              </div>
            </div>
          }
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowLocationDialog(false)}>{t("cancel", "Cancel")}

            </Button>
            <Button onClick={() => setShowLocationDialog(false)}>{t("confirmLocation", "Confirm Location")}

            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>);

};